// « Mes horaires » (appli employé) : une carte par jour, bien séparée,
// et pour chaque jour travaillé « Qui travaille ce jour » (Matin / Après-midi, sans doublon).
import { useState, useEffect } from 'react'
import MobileLayout from '../../components/MobileLayout'
import { employeeApi } from '../../lib/employeeApi'

interface Shift {
  id: string
  date: string
  startTime: string
  endTime: string
  breakMinutes: number
  note?: string
}
interface Person { id: string; name: string; initial: string; color: string | null; me: boolean; slots: { start: string; end: string }[] }
interface DayTeam { date: string; matin: Person[]; aprem: Person[] }

const DAYS_LONG = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']
const MONTHS = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sep', 'oct', 'nov', 'déc']

function mondayOf(iso: string) {
  const d = new Date(iso + 'T12:00:00')
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  return d.toISOString().slice(0, 10)
}
function addDays(iso: string, n: number) {
  const d = new Date(iso + 'T12:00:00')
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}
function netH(s: Shift) {
  const [sh, sm] = s.startTime.split(':').map(Number)
  const [eh, em] = s.endTime.split(':').map(Number)
  return ((eh * 60 + em) - (sh * 60 + sm) - (s.breakMinutes || 0)) / 60
}
const isMorning = (start: string) => start < '12:00'

export default function MobilePlanning() {
  const [today, setToday] = useState(() => new Date().toLocaleDateString('fr-CA', { timeZone: 'Europe/Paris' }))
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date().toLocaleDateString('fr-CA', { timeZone: 'Europe/Paris' })))
  const [shifts, setShifts] = useState<Shift[]>([])
  const [loading, setLoading] = useState(false)
  const [myColor, setMyColor] = useState<string | null>(null)
  const [openDay, setOpenDay] = useState<string | null>(null)
  const [team, setTeam] = useState<DayTeam | null>(null)
  const [loadingTeam, setLoadingTeam] = useState(false)

  useEffect(() => {
    employeeApi.get('/employee/me/server-time').then(r => {
      if (r.data?.today) { setToday(r.data.today); setWeekStart(mondayOf(r.data.today)) }
    }).catch(() => {})
    employeeApi.get('/employee/me').then(r => setMyColor(r.data?.rowColor || null)).catch(() => {})
  }, [])

  useEffect(() => {
    setLoading(true)
    employeeApi.get(`/employee/me/planning?weekStart=${weekStart}`)
      .then(r => setShifts(r.data || []))
      .catch(() => setShifts([]))
      .finally(() => setLoading(false))
  }, [weekStart])

  function toggleTeam(day: string) {
    if (openDay === day) { setOpenDay(null); return }
    setOpenDay(day)
    setTeam(null)
    setLoadingTeam(true)
    employeeApi.get(`/employee/me/day-team?date=${day}`)
      .then(r => setTeam(r.data))
      .catch(() => setTeam(null))
      .finally(() => setLoadingTeam(false))
  }

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
  const totalH = shifts.reduce((a, s) => a + netH(s), 0)
  const end = addDays(weekStart, 6)
  const label = (iso: string) => { const d = new Date(iso + 'T12:00:00'); return `${d.getDate()} ${MONTHS[d.getMonth()]}` }

  const TeamBlock = ({ title, list, tone }: { title: string; list: Person[]; tone: string }) => (
    <div>
      <p className={`text-xs font-bold uppercase tracking-wide px-2 py-1 rounded-md mb-1.5 ${tone}`}>{title} · {list.length}</p>
      {list.length === 0 ? <p className="text-xs text-gray-400 px-2">Personne</p> : (
        <div className="space-y-1">
          {list.map(p => (
            <div key={p.id} className={`flex items-center justify-between rounded-lg px-3 py-2 border border-gray-900/20 ${p.me ? 'ring-2 ring-blue-600' : ''}`}
              style={{ backgroundColor: p.color || '#F3F4F6' }}>
              <span className="text-sm font-bold text-gray-900">{p.name}{p.me ? ' (moi)' : ''}</span>
              <span className="text-sm font-semibold text-gray-900 tabular-nums">{p.slots.map(s => `${s.start}–${s.end}`).join(' / ')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <MobileLayout>
      <div className="p-3 space-y-3">
        {/* Semaine */}
        <div className="bg-white rounded-2xl shadow-sm px-2 py-2 flex items-center justify-between">
          <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="w-10 h-10 rounded-xl text-gray-600 text-lg">←</button>
          <div className="text-center">
            <div className="font-semibold text-gray-800">{label(weekStart)} – {label(end)}</div>
            <div className="text-xs text-blue-600">{totalH.toFixed(1)}h cette semaine</div>
          </div>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="w-10 h-10 rounded-xl text-gray-600 text-lg">→</button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement…</div>
        ) : days.map(day => {
          const list = shifts.filter(s => s.date === day).sort((a, b) => a.startTime.localeCompare(b.startTime))
          const d = new Date(day + 'T12:00:00')
          const isToday = day === today
          const works = list.length > 0
          const am = list.filter(s => isMorning(s.startTime))
          const pm = list.filter(s => !isMorning(s.startTime))
          return (
            <div key={day} className={`rounded-xl overflow-hidden border-2 border-gray-900 bg-white ${isToday ? 'ring-4 ring-blue-400' : ''}`}>
              {/* Bandeau du jour : couleur de l'employé si travaillé, gris si repos */}
              <div className="px-3 py-2 flex items-center justify-between border-b-2 border-gray-900"
                style={{ backgroundColor: works ? (myColor || '#BFDBFE') : '#E5E7EB' }}>
                <span className="font-bold text-gray-900">
                  {DAYS_LONG[d.getDay()]} {d.getDate()} {MONTHS[d.getMonth()]}
                  {isToday && <span className="ml-2 text-[11px] bg-blue-600 text-white px-2 py-0.5 rounded-full align-middle">Aujourd’hui</span>}
                </span>
                <span className="text-sm font-semibold text-gray-900">{works ? `${list.reduce((a, s) => a + netH(s), 0).toFixed(1)}h` : 'Repos'}</span>
              </div>

              {works && (
                <div className="divide-y divide-gray-200">
                  {am.length > 0 && (
                    <div className="px-3 py-2 flex items-center justify-between">
                      <span className="text-sm font-semibold text-amber-700">☀ Matin</span>
                      <span className="text-lg font-bold text-gray-900 tabular-nums">{am.map(s => `${s.startTime} – ${s.endTime}`).join(' / ')}</span>
                    </div>
                  )}
                  {pm.length > 0 && (
                    <div className="px-3 py-2 flex items-center justify-between">
                      <span className="text-sm font-semibold text-indigo-700">🌙 Après-midi</span>
                      <span className="text-lg font-bold text-gray-900 tabular-nums">{pm.map(s => `${s.startTime} – ${s.endTime}`).join(' / ')}</span>
                    </div>
                  )}
                  {list.some(s => s.note) && (
                    <p className="px-3 py-1.5 text-xs text-gray-500 italic">{list.filter(s => s.note).map(s => s.note).join(' · ')}</p>
                  )}
                  <button onClick={() => toggleTeam(day)}
                    className={`w-full py-2.5 text-sm font-semibold ${openDay === day ? 'bg-gray-900 text-white' : 'bg-gray-50 text-blue-700'}`}>
                    {openDay === day ? 'Masquer l’équipe' : '👥 Qui travaille ce jour'}
                  </button>
                  {openDay === day && (
                    <div className="p-3 space-y-3 bg-gray-50">
                      {loadingTeam ? <p className="text-sm text-gray-400 text-center">Chargement…</p>
                        : !team ? <p className="text-sm text-gray-400 text-center">Indisponible</p>
                        : <>
                          <TeamBlock title="☀ Matin" list={team.matin} tone="bg-amber-100 text-amber-900" />
                          <TeamBlock title="🌙 Après-midi" list={team.aprem} tone="bg-indigo-100 text-indigo-900" />
                        </>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </MobileLayout>
  )
}
